import { type DisplayStatusType } from '@constants'
import { type WpOrderStatus, type DeliveryService, type ECommerceOrder, type OrderInfo, type OriginalOrderStatus, type PaymentTransactionStatus, type ServiceOrderItem, type Subscription, type RefundStatus, SUBSCRIPTION_STATUS, WP_ORDER_STATUS, WP_ORDER_TYPE } from '@hooks-api/types/dataModel/eCommerceOrders'
import { type ShippingLabelProvider } from '@hooks-api/types/dataModel/fpOrder'
import { deriveDisplayStatus } from '@utils/derive-display-status'
import { formatDate } from '@utils/formatDate'
import { format } from 'date-fns'

export interface RelatedOrderDTO {
  orderId: string
  productName: string
  quantity: number
  status: WpOrderStatus
  deliveryDate: string
  remainDeliveryIndex?: number
  shippingLabelProvider: ShippingLabelProvider[]
}

export interface OrderListDTO {
  id?: number
  title: string
  subtitle?: string
  estimatedDeliveryDate?: string
  nextDeliveryDate?: string
  images: string[]
  /** 2B 站台顯示用的商品圖 */
  b2bImages: string[]
  orderId: string
  fortuneOrderId?: string
  parentProductId?: string
  quantity: number
  total: number
  remainQuantity?: number
  orderCreatedTime: string
  relatedOrderCreatedTime?: string
  nextDeliveryQuantity?: number
  displayNextDeliveryQuantity?: number
  displayNextDeliveryDate?: string
  relatedOrders?: RelatedOrderDTO[]
  /** 是否為預產訂單 true 為預產訂單，false 為首單 */
  isAutoRenew?: boolean
  isPayable?: boolean
  /**
   * 是否為訂閱/寄倉訂單
   */
  isSubscriptionOrder?: boolean
  /** 是否為已取消的寄倉訂單 */
  isCancelledServiceOrder?: boolean
  isPrecreateStatus?: boolean
  remainDeliveryAt?: string[]
  serviceOrderItems?: ServiceOrderItem[]
  subscriptionStatus?: Subscription['status']
  paymentFailedCount?: Subscription['payment_failed_count']
  paymentTransactionStatus?: PaymentTransactionStatus | null
  /** 是否為寄倉、訂閱訂單的子訂單 */
  isChildOrder?: boolean
  nextPaymentAt?: string
  originalStatus?: OriginalOrderStatus
  currency: ECommerceOrder['currency']
  periodUnit?: string
  periodValue?: number
  transactionId?: string | null
  chargeAt?: string
  subscriptionTotal?: number
  productId?: number
  variationId?: number
  transactionHash: string
  campaignId?: number | null
  orderStatus?: WpOrderStatus
  paymentTokenId?: number | null
  creditCardLast4?: string | null
  refundStatus?: RefundStatus | null
  displayStatus?: DisplayStatusType
}

const MAX_IMAGE_COUNT = 4

function transformFastpassOrder(orderInfo: OrderInfo, relatedOrders: OrderInfo[], transactionId: OrderListDTO['transactionId'], total: OrderListDTO['total'], transactionHash: OrderListDTO['transactionHash']): Omit<OrderListDTO, 'currency'> {
  const title = relatedOrders.flatMap(({ order_items }) => order_items).map(({ product_name }) => product_name).join('、')
  const orderId = relatedOrders.map(({ order_id }) => order_id).join(', ')
  // @ts-expect-error - TODO: fix this
  const orderCreatedTime = relatedOrders[0].order_created_at ? format(new Date(relatedOrders[0].order_created_at), 'yyyy-MM-dd') : '-'
  const quantity = relatedOrders.flatMap(({ order_items }) => order_items).reduce((acc, { quantity }) => acc + quantity, 0)
  const eligibleOrderItems = (relatedOrders.at(-1)?.order_items ?? []).filter(({ is_giveaway }) => is_giveaway === 0).slice(0, MAX_IMAGE_COUNT)
  const images = eligibleOrderItems.map(({ product_image }) => product_image)
  const b2bImages = eligibleOrderItems.map(item => item.last_gallery_image || item.product_image)
  const orderStatus = orderInfo.status
  return {
    title,
    orderId,
    orderCreatedTime,
    quantity,
    total,
    images,
    b2bImages,
    transactionId,
    transactionHash,
    orderStatus,
  }
}

function transFormSubscribeOrder(orderInfo: OrderInfo, deliveryService: DeliveryService, relatedOrders: OrderInfo[], transactionId: OrderListDTO['transactionId'], transactionHash: OrderListDTO['transactionHash'], ordersTotalSum?: number, subscription?: Subscription, isPayable?: boolean): Omit<OrderListDTO, 'currency'> {
  const allOrders = [...relatedOrders, orderInfo]
  const firstProcessingOrder = relatedOrders.find(({ status }) => status === WP_ORDER_STATUS.PROCESSING)
  const title = deliveryService.service_order_items[0]?.product_name || '-'
  const subtitle = relatedOrders.at(-1)?.order_items?.[0]?.variation_name || '-'
  /** Fastpass Id */
  const id = orderInfo.id
  /** WordPress Id */
  const orderId = orderInfo.order_id || '-'
  /** 用於取消訂閱 */
  const fortuneOrderId = String(orderInfo.id)
  /** 用於更新寄倉訂單配送資訊 */
  const parentProductId = String(deliveryService.service_order_items[0]?.parent_product_id)
  const orderCreatedTime = orderInfo.order_created_at ? format(new Date(orderInfo.order_created_at), 'yyyy-MM-dd') : '-'
  const relatedOrderCreatedTime = relatedOrders[0]?.order_created_at ? format(new Date(relatedOrders[0]?.order_created_at), 'yyyy-MM-dd') : '-'
  const quantity = deliveryService.service_order_items[0]?.total_quantity ?? 0
  const itemsSum = orderInfo.order_items.reduce((acc, cur) => acc + cur.total, 0)
  const total = ordersTotalSum ?? itemsSum
  const subscriptionTotal = ordersTotalSum ?? itemsSum
  const remainQuantity = deliveryService.service_order_items[0]?.remain_quantity ?? 0
  const eligibleOrderItems = (relatedOrders.at(-1)?.order_items ?? []).filter(({ is_giveaway }) => is_giveaway === 0).slice(0, MAX_IMAGE_COUNT)
  const images = eligibleOrderItems.map(({ product_image }) => product_image)
  const b2bImages = eligibleOrderItems.map(item => item.last_gallery_image || item.product_image)
  const nextDeliveryQuantity = firstProcessingOrder?.order_items.filter(({ is_giveaway }) => is_giveaway === 0)[0]?.quantity ?? 0
  /** 不管訂單是否為完成或進行中，都應該顯示下一次配送數量 */
  const displayNextDeliveryQuantity = allOrders?.filter(({ status }) => status !== WP_ORDER_STATUS.COMPLETED)[0]?.order_items.filter(({ is_giveaway }) => is_giveaway === 0)[0]?.quantity ?? 0
  /** 是否為預產訂單 true 為預產訂單，false 為首單 */
  const isAutoRenew = !!subscription?.is_auto_renew
  /** 是否為可付款的寄倉訂單 */
  const isSubscriptionOrder = !!subscription
  /** 是否為預產狀態的寄倉訂單 */
  const isPrecreateStatus = orderInfo.is_precreate_status
  /** 是否為已取消的寄倉訂單 */
  const isCancelledServiceOrder = subscription?.status === SUBSCRIPTION_STATUS.CANCELLED
  const isChildOrder = orderInfo.is_child_order
  const remainDeliveryAt = deliveryService.remain_delivery_at
  const serviceOrderItems = deliveryService.service_order_items
  const periodUnit = deliveryService.period_unit
  const periodValue = deliveryService.period_value
  const subscriptionStatus = subscription?.status
  const paymentFailedCount = subscription?.payment_failed_count
  const nextPaymentAt = formatDate(subscription?.next_payment_at)
  const firstProcessingOrderDate = firstProcessingOrder?.delivery_date
  const estimatedDeliveryDate = formatDate(firstProcessingOrderDate)
  const firstNextDeliveryDate = relatedOrders.filter(({ status }) => status !== WP_ORDER_STATUS.COMPLETED)[0]?.delivery_date
  const nextDeliveryDate = formatDate(firstNextDeliveryDate)
  /** NOTE: 如果有找到 Processing 訂單，則取第 0 筆，沒有則取 nextDeliveryDate，若都沒有則取 deliveryService.remain_delivery_at?.[0] */
  const rawDisplayNextDeliveryDate = firstProcessingOrderDate
    ? firstProcessingOrderDate
    : nextDeliveryDate
      ?? deliveryService.remain_delivery_at?.[0]
  const displayNextDeliveryDate = formatDate(rawDisplayNextDeliveryDate)
  const originalStatus = orderInfo.original_status
  const _relatedOrders: RelatedOrderDTO[] = []
  const chargeAt = subscription?.charge_at ? format(new Date(subscription.charge_at), 'yyyy-MM-dd') : undefined
  /** NOTE: order_items[0] 有可能是贈品，需要過濾 */
  const productId = relatedOrders[0]?.order_items.filter(({ is_giveaway }) => is_giveaway === 0)[0]?.product_id
  /** NOTE: order_items[0] 有可能是贈品，需要過濾 */
  const variationId = relatedOrders[0]?.order_items.filter(({ is_giveaway }) => is_giveaway === 0)[0]?.variation_id
  let remainDeliveryIndex = 0
  /**
   * NOTE: deliveryService.remain_delivery_at 只會有 pending 以及 uncreated 的 order
   * 在使用 useApiUpdateOrders 時，將要改變的 index 記錄下來，透過 array.splice 刪除原本的 date 並插入新的 date
   */
  relatedOrders.forEach(({ order_items, order_id, status, shipping_label_providers, delivery_date }, index) => {
    const isPendingOrder = status === WP_ORDER_STATUS.PENDING || status === WP_ORDER_STATUS.UNCREATED
    _relatedOrders[index] = {
      orderId: order_id,
      productName: order_items.filter(({ is_giveaway }) => is_giveaway === 0)?.[0]?.variation_name || '-',
      // @ts-expect-error - TODO: fix this
      quantity: order_items.filter(({ is_giveaway }) => is_giveaway === 0)?.[0].quantity ?? 0,
      status,
      deliveryDate: delivery_date ? formatDate(delivery_date) : '-',
      remainDeliveryIndex: undefined,
      shippingLabelProvider: shipping_label_providers,
    }
    if (isPendingOrder) {
      _relatedOrders[index].remainDeliveryIndex = remainDeliveryIndex
      remainDeliveryIndex += 1
    }
  })
  const orderStatus = orderInfo.status

  return {
    id,
    fortuneOrderId,
    title,
    subtitle,
    orderId,
    parentProductId,
    productId,
    variationId,
    orderCreatedTime,
    relatedOrderCreatedTime,
    quantity,
    total,
    remainQuantity,
    images,
    b2bImages,
    nextDeliveryQuantity,
    displayNextDeliveryQuantity,
    displayNextDeliveryDate,
    relatedOrders: _relatedOrders,
    isAutoRenew,
    isPayable,
    isSubscriptionOrder,
    isPrecreateStatus,
    remainDeliveryAt,
    serviceOrderItems,
    subscriptionStatus,
    paymentFailedCount,
    nextPaymentAt,
    estimatedDeliveryDate,
    nextDeliveryDate,
    originalStatus,
    periodUnit,
    periodValue,
    chargeAt,
    subscriptionTotal,
    transactionId,
    transactionHash,
    isCancelledServiceOrder,
    isChildOrder,
    orderStatus,
  }
}

function transFormNormalOrder(orderInfo: OrderInfo, transactionId: OrderListDTO['transactionId'], total: OrderListDTO['total'], transactionHash: OrderListDTO['transactionHash']): Omit<OrderListDTO, 'currency'> {
  const title = orderInfo.order_items?.filter(({ is_giveaway }) => is_giveaway === 0).map(({ product_name }) => product_name).join('、') || '-'
  /** WordPress Id */
  const orderId = orderInfo.order_id
  /** Fastpass Id */
  const id = orderInfo.id
  const orderCreatedTime = orderInfo.order_created_at ? format(new Date(orderInfo.order_created_at), 'yyyy-MM-dd') : '-'
  const quantity = orderInfo.order_items?.reduce((acc, { quantity }) => acc + quantity, 0)
  const eligibleOrderItems = orderInfo.order_items.filter(({ is_giveaway }) => is_giveaway === 0).slice(0, MAX_IMAGE_COUNT)
  const images = eligibleOrderItems.map(({ product_image }) => product_image)
  const b2bImages = eligibleOrderItems.map(item => item.last_gallery_image || item.product_image)
  const originalStatus = orderInfo.original_status
  const fortuneOrderId = String(orderInfo.id)
  const orderStatus = orderInfo.status
  return {
    id,
    title,
    orderId,
    orderCreatedTime,
    quantity,
    total,
    images,
    b2bImages,
    originalStatus,
    transactionId,
    transactionHash,
    fortuneOrderId,
    orderStatus,
  }
}

export function transformOrder({ delivery_service, subscription, related_orders, ...order }: ECommerceOrder): OrderListDTO {
  const transactionId = order.payment_transaction_id
  const transactionHash = order.transaction_hash
  const ordersTotalSum = order.orders_total_sum
  const orderTotal = ordersTotalSum ?? order?.total ?? 0
  const campaignId = order.campaign_id
  const isPayable = order?.is_payable ?? false
  const refundStatus = order.refund_status
  // 暫解：後端尚未回傳 display_status 時，前端根據 order status 推導
  const displayStatus = order.order_info.display_status ?? deriveDisplayStatus({
    status: order.order_info.status,
    refundStatus: order.refund_status,
    relatedOrderStatuses: related_orders?.map(relatedOrder => relatedOrder.status),
  })
  const emptyData = {
    title: '',
    orderId: '',
    orderCreatedTime: '',
    quantity: 0,
    total: 0,
    images: [],
    b2bImages: [],
    currency: '',
    transactionHash: '',
    campaignId: null,
    displayStatus: undefined,
  }
  if (order.order_type === WP_ORDER_TYPE.PAYMENT_TRANSACTION) {
    if (!related_orders) return emptyData
    return { ...transformFastpassOrder(order.order_info, related_orders, transactionId, orderTotal, transactionHash), currency: order.currency, campaignId, isPayable, refundStatus, displayStatus }
  }
  if (order.order_type === WP_ORDER_TYPE.CAMPAIGN) {
    if (!related_orders) return emptyData
    return { ...transformFastpassOrder(order.order_info, related_orders, transactionId, orderTotal, transactionHash), currency: order.currency, campaignId, isPayable, refundStatus, displayStatus }
  }
  if (order.order_type === WP_ORDER_TYPE.NORMAL) {
    return { ...transFormNormalOrder(order.order_info, transactionId, orderTotal, transactionHash), currency: order.currency, campaignId, isPayable, refundStatus, displayStatus }
  }
  if (order.order_type === WP_ORDER_TYPE.SUBSCRIBE || order.order_type === WP_ORDER_TYPE.SERVICE_ORDER) {
    if (!related_orders || !delivery_service) return emptyData
    return { ...transFormSubscribeOrder(order.order_info, delivery_service, related_orders, transactionId, transactionHash, ordersTotalSum, subscription, isPayable), currency: order.currency, campaignId, paymentTokenId: order.payment_token_id, creditCardLast4: order.last4, displayStatus, paymentTransactionStatus: order.payment_transaction_status }
  }

  return emptyData
}

export function transformOrderData(data: ECommerceOrder[]): OrderListDTO[] {
  return data.map(eCommerceOrder => transformOrder(eCommerceOrder))
}
